import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';

import { FuseConfigService } from '@fuse/services/config.service';
import { fuseAnimations } from '@fuse/animations';
import { APIService, CreateUserInput } from 'app/api/API.service';
import { AppSyncErrorService } from 'app/appsync-error.service';
import { AmplifyService } from 'aws-amplify-angular';
import * as slug from 'slug';

@Component({
    selector: 'register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class RegisterComponent implements OnInit, OnDestroy {
    registerForm: FormGroup;
    errorMessage: string;
    success: boolean;
    loading: boolean;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        protected api: APIService,
        protected errorService: AppSyncErrorService,
        protected amplify: AmplifyService,
        private _fuseConfigService: FuseConfigService,
        private _formBuilder: FormBuilder
    ) {
        // Set logger
        this.logger = this.amplify.logger('RegisterComponent');
        this.success = false;

        // Configure the layout
        this._fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                },
                footer: {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.registerForm = this._formBuilder.group({
            firstname: ['', [
                Validators.required,
                Validators.pattern('^[a-zA-ZÀ-ž\\s.-]{2,50}$')
            ]],
            lastname: ['', [
                Validators.required,
                Validators.pattern('^[a-zA-ZÀ-ž\\s.-]{2,50}$')
            ]],
            email: ['', [Validators.required, Validators.email]],
            userCompany: [''],
            password: ['', [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern(/[A-Z]+/),
                Validators.pattern(/[0-9]+/),
                Validators.pattern(/[a-z]+/),
                Validators.pattern(/[^A-Za-z0-9]+/)
            ]]
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    createUsername(input: CreateUserInput): string {
        // tslint:disable-next-line: no-bitwise
        const postfix = (Math.random() * 0xFFFFFF << 0).toString(16);
        return slug(`${input.firstname.toLowerCase()} ${input.lastname.toLowerCase()} ${postfix}`) as string;
    }

    onSubmit(): void {
        this.errorMessage = '';
        this.loading = true;

        const input = this.registerForm.value as CreateUserInput;
        const signUpData = {
            username: this.createUsername(input),
            password: input.password,
            attributes: {
                'email': input.email,
                'given_name': input.firstname,
                'family_name': input.lastname,
                'custom:userCompany': input.userCompany
            }
        };

        this.amplify.auth()
            .signUp(signUpData)
            .then(result => {
                this.success = true;
            })
            .catch(err => {
                this.logger.error(err);
                this.errorMessage = this.errorService.getMessage(err);
            })
            .finally(() => {
                this.loading = false;
            });
    }
}

